import React, { useState, useMemo } from 'react';
import { Option } from '@material-tailwind/react';
import { Menu, MenuHandler, MenuItem, MenuList, IconButton } from '@material-tailwind/react';
import Input from '@components/forms/Input';
import Button from '@components/forms/Button';
import Select from '@components/forms/Select';
import StatusChip from '@components/widgets/StatusChip';
import Modal from '@components/widgets/Modal';
import { ReactComponent as SearchIcon } from '@assets/icons/search_icon.svg';
import { ReactComponent as MoreVertIcon } from '@assets/icons/more-icon.svg';
import { format, parseISO } from 'date-fns';
import type { BrokerOrgMember, BrokerOrgMemberRole } from '@types';

interface Props {
  members: BrokerOrgMember[];
  currentUserId: string;
  isAdmin: boolean;
  onChangeRole: (userId: string, role: BrokerOrgMemberRole) => void;
  onRemove: (userId: string) => void;
  onResendInvite: (userId: string) => void;
}

const ROLE_OPTIONS = [
  { label: 'All roles', value: 'all' },
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Member', value: 'MEMBER' },
];

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Pending', value: 'PENDING' },
];

const MembersTab = ({ members, currentUserId, isAdmin, onChangeRole, onRemove, onResendInvite }: Props) => {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [appliedRole, setAppliedRole] = useState('all');
  const [appliedStatus, setAppliedStatus] = useState('all');
  const [removeTarget, setRemoveTarget] = useState<BrokerOrgMember | null>(null);
  const [changeRoleTarget, setChangeRoleTarget] = useState<BrokerOrgMember | null>(null);
  const [pendingRole, setPendingRole] = useState<BrokerOrgMemberRole>('MEMBER');

  const filtered = useMemo(() => {
    return members.filter((m) => {
      const matchesQuery =
        !appliedQuery ||
        m.email.toLowerCase().includes(appliedQuery.toLowerCase()) ||
        `${m.firstName ?? ''} ${m.lastName ?? ''}`.toLowerCase().includes(appliedQuery.toLowerCase());
      const matchesRole = appliedRole === 'all' || m.role === appliedRole;
      const matchesStatus = appliedStatus === 'all' || m.status === appliedStatus;
      return matchesQuery && matchesRole && matchesStatus;
    });
  }, [members, appliedQuery, appliedRole, appliedStatus]);

  const runFilter = () => {
    setAppliedQuery(query);
    setAppliedRole(roleFilter);
    setAppliedStatus(statusFilter);
  };

  const clearFilters = () => {
    setQuery(''); setRoleFilter('all'); setStatusFilter('all');
    setAppliedQuery(''); setAppliedRole('all'); setAppliedStatus('all');
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 py-4">
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Search</p>
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 -translate-y-1/2 text-grey-500 z-10" />
            <Input
              inputClassName="!h-10 pl-10 text-sm"
              placeholder="Search name or email"
              value={query}
              onChange={(e: any) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Role</p>
          <Select className="!h-10 text-sm w-36" value={roleFilter} onChange={(v: any) => setRoleFilter(v)}
            selected={(el) => el && React.cloneElement(el, { className: 'flex items-center px-0 gap-2 pointer-events-none' })}>
            {ROLE_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </div>
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Status</p>
          <Select className="!h-10 text-sm w-36" value={statusFilter} onChange={(v: any) => setStatusFilter(v)}
            selected={(el) => el && React.cloneElement(el, { className: 'flex items-center px-0 gap-2 pointer-events-none' })}>
            {STATUS_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </div>
        <Button className="!bg-black !text-white h-10 text-sm font-semibold px-4 rounded-lg capitalize" onClick={runFilter}>
          Run filter
        </Button>
        <Button variant="text" className="bg-white h-10 text-sm font-semibold px-4 rounded-lg text-grey-600 capitalize hover:bg-blue-500/10" onClick={clearFilters}>
          Clear filters
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-grey-50 border-b border-grey-200">
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Username</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Name</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Role</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Last Login</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Status</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.userId} className="border-b border-grey-100 hover:bg-grey-50">
                <td className="px-3 py-4 font-medium text-grey-900">{m.email}</td>
                <td className="px-3 py-4 text-grey-600">
                  {m.firstName || m.lastName ? `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() : '—'}
                </td>
                <td className="px-3 py-4">
                  <span className="text-sm text-grey-700">{m.role === 'ADMIN' ? 'Admin' : 'Member'}</span>
                </td>
                <td className="px-3 py-4 text-grey-500">
                  {m.lastLogin ? format(parseISO(m.lastLogin), 'dd/MM/yyyy h:mma') : '—'}
                </td>
                <td className="px-3 py-4">
                  <StatusChip variant={m.status === 'ACTIVE' ? 'success' : 'info3'} label={m.status === 'ACTIVE' ? 'Active' : 'Pending'} />
                </td>
                <td className="px-3 py-4">
                  {isAdmin && m.userId !== currentUserId && (
                    <Menu>
                      <MenuHandler>
                        <IconButton variant="text" size="sm"><MoreVertIcon /></IconButton>
                      </MenuHandler>
                      <MenuList>
                        <MenuItem className="font-medium text-grey-700 text-sm" onClick={() => { setChangeRoleTarget(m); setPendingRole(m.role); }}>
                          Change Role
                        </MenuItem>
                        {m.status === 'PENDING' && (
                          <MenuItem className="font-medium text-grey-700 text-sm" onClick={() => onResendInvite(m.userId)}>
                            Resend Invitation
                          </MenuItem>
                        )}
                        <MenuItem className="font-medium text-rose-700 text-sm" onClick={() => setRemoveTarget(m)}>
                          Remove from Org
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-grey-400 text-sm">No members match the current filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal title="Remove from Organisation" size="md" open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <div className="flex flex-col gap-4 p-2">
          <p className="text-grey-700 text-sm">
            Are you sure you want to remove{' '}
            <span className="font-semibold text-grey-900">{removeTarget?.email}</span>{' '}
            from this broker organisation?
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outlined" className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 bg-white" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button className="bg-primary text-white normal-case font-semibold rounded-lg text-sm py-2" onClick={() => { onRemove(removeTarget!.userId); setRemoveTarget(null); }}>
              Remove
            </Button>
          </div>
        </div>
      </Modal>

      <Modal title="Change Member Role" size="md" open={!!changeRoleTarget} onClose={() => setChangeRoleTarget(null)}>
        <div className="flex flex-col gap-4 p-2">
          <div>
            <p className="text-sm font-medium text-grey-700 mb-1.5">Email</p>
            <Input readOnly value={changeRoleTarget?.email ?? ''} />
          </div>
          <div>
            <p className="text-sm font-medium text-grey-700 mb-1.5">Role</p>
            <Select className="w-full" value={pendingRole} onChange={(v: any) => setPendingRole(v)}>
              <Option value="ADMIN">Admin</Option>
              <Option value="MEMBER">Member</Option>
            </Select>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outlined" className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 bg-white" onClick={() => setChangeRoleTarget(null)}>
              Cancel
            </Button>
            <Button className="bg-primary text-white normal-case font-semibold rounded-lg text-sm py-2 px-4" onClick={() => { onChangeRole(changeRoleTarget!.userId, pendingRole); setChangeRoleTarget(null); }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MembersTab;
